import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BrandLogo } from "@/components/site/BrandLogo";
import { Inbox, UserCheck, Footprints, BadgeCheck, ScrollText, Search, Phone, Mail, Scale, CalendarDays, ArrowUpRight, FolderOpen, CheckCircle2, Zap, ChevronDown, Download, CreditCard } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const OPEN = ["pending", "in_progress"];
const STEPS = [
  { key: "received", label: "Received", Icon: Inbox },
  { key: "assigned", label: "Assigned", Icon: UserCheck },
  { key: "attempting", label: "Out Serving", Icon: Footprints },
  { key: "served", label: "Served", Icon: BadgeCheck },
  { key: "affidavit", label: "Affidavit", Icon: ScrollText },
];
const KIND_LABEL = { asset_search: "Asset search", record_retrieval: "Record retrieval", document_courier: "Drop-off / pick-up" };
const fmt = (iso, tz) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: tz || undefined }) : null);
const fmtTime = (iso, tz) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz || undefined }) : null);

const stageOf = (j) => {
  if (["non_served", "cancelled", "unable"].includes(j.status)) return { idx: 2, terminal: true };
  if (j.affidavit_released) return { idx: 4 };
  if (["served", "completed"].includes(j.status)) return { idx: 3 };
  if (j.attempts > 0) return { idx: 2 };
  if (j.server_assigned) return { idx: 1 };
  return { idx: 0 };
};

export default function Portal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("open");
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/portal/${token}`).then((r) => { setData(r.data); setTimeout(() => setMounted(true), 60); }).catch((e) => setErr(e?.response?.data?.detail || "This link is not available."));
  }, [token]);

  if (err) return <div className="trk-root"><div className="trk-hero" /><main className="trk-body"><div className="trk-card text-center py-16" data-testid="portal-error"><h1 className="text-2xl font-bold">Nobody's home at this link.</h1><p className="text-slate-500 mt-2">{err}</p></div></main></div>;
  if (!data) return <div className="trk-root"><div className="trk-hero" /></div>;

  const tz = data.org.timezone;
  const open = data.jobs.filter((j) => OPEN.includes(j.status));
  const done = data.jobs.filter((j) => !OPEN.includes(j.status));
  const list = (tab === "open" ? open : done).filter((j) => !q || `${j.job_number} ${j.recipient_name} ${j.court_case_number || ""} ${j.client_job_no || ""}`.toLowerCase().includes(q.toLowerCase()));
  const headline = open.length === 0 ? "Everything's served. Nothing on your desk — or ours." : open.length === 1 ? "One job in motion. We're on it." : `${open.length} jobs in motion. We're on all of them.`;

  return (
    <div className="trk-root" data-testid="client-portal">
      <header className="trk-hero">
        <div className="flex items-center gap-3 mb-8">
          {data.org.is_site_org ? <BrandLogo className="h-20 sm:h-24 w-auto -ml-3 drop-shadow-[0_14px_30px_rgba(0,0,0,.45)]" /> : data.org.has_logo ? <img src={data.org.logo_url} alt="" className="h-10 w-10 rounded-lg bg-white object-contain p-1" /> : <div className="h-10 w-10 rounded-lg bg-[#e8b04b] grid place-items-center font-black text-[#0b1f3a]">{data.org.name[0]}</div>}
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#e8b04b] font-semibold">{data.org.name}</div>
            <div className="text-xs text-slate-300">Client Portal · {data.client_name}</div>
          </div>
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05] tracking-tight max-w-3xl" data-testid="portal-headline">{headline}</h1>
        <p className="mt-3 text-slate-300 text-sm sm:text-base max-w-xl">Every job for <span className="text-white font-semibold" data-testid="portal-client-name">{data.client_name}</span>, live. Tap any job to open its full tracker — no need to call in for a status check.</p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full bg-white/10 border border-white/15 p-1 text-sm backdrop-blur">
            {[["open", "Open", open.length, FolderOpen], ["done", "Finished", done.length, CheckCircle2]].map(([k, l, n, Icon]) => (
              <button key={k} type="button" onClick={() => setTab(k)} className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full transition-colors ${tab === k ? "bg-white text-[#0b1f3a] font-semibold" : "text-slate-200 hover:text-white"}`} data-testid={`portal-tab-${k}`}>
                <Icon className="h-3.5 w-3.5" />{l}<span className={`text-xs font-mono ${tab === k ? "text-[#1f5fa8]" : "text-slate-400"}`} data-testid={k === "open" ? "portal-open-count" : undefined}>{n}</span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[240px] max-w-sm">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search job #, party, case…" className="w-full h-10 pl-10 pr-4 rounded-full bg-white/10 border border-white/15 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:bg-white focus:text-[#0b1f3a] focus:placeholder:text-slate-400 transition-colors" data-testid="portal-search" />
          </div>
        </div>
      </header>

      <main className="trk-body">
        {list.length === 0 ? (
          <div className="trk-card text-center py-14 text-slate-500" data-testid="portal-empty">{tab === "open" ? "Nothing open right now. Enjoy the quiet." : "No finished jobs yet."}</div>
        ) : list.map((j, i) => <JobCard key={j.job_number} j={j} tz={tz} mounted={mounted} delay={i * 60} />)}
        <div className="trk-foot text-sm text-slate-500">
          <span>You'll see updates here the moment our server logs them. Really — no need to check in.</span>
          <span className="flex flex-wrap items-center gap-4 font-semibold text-[#0b1f3a]">
            {data.org.phone && <a href={`tel:${data.org.phone}`} className="inline-flex items-center gap-1.5"><Phone className="h-4 w-4" />{data.org.phone}</a>}
            {data.org.email && <a href={`mailto:${data.org.email}`} className="inline-flex items-center gap-1.5"><Mail className="h-4 w-4" />{data.org.email}</a>}
          </span>
        </div>
      </main>
    </div>
  );
}

function JobCard({ j, tz, mounted, delay }) {
  const [open, setOpen] = useState(false);
  const { idx, terminal } = stageOf(j);
  const steps = j.job_kind === "service" ? STEPS : STEPS.slice(0, 4);
  const pct = terminal ? 50 : (idx / (steps.length - 1)) * 100;
  const terminalLabel = j.status === "cancelled" ? "Closed" : j.job_kind === "service" ? "Not Served" : "Not Completed";
  const recent = j.recent_attempts || [];
  return (
    <div className="trk-card group transition-[transform,box-shadow] hover:shadow-[0_18px_40px_-20px_rgba(11,31,58,.5)]" style={{ animation: "trkIn .5s ease forwards", animationDelay: `${delay}ms`, opacity: 0 }} data-testid={`portal-job-${j.job_number}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-mono text-[#1f5fa8]">{j.job_number}</span>
            {j.job_kind !== "service" && <span className="trk-pill">{KIND_LABEL[j.job_kind]}</span>}
            {j.priority && j.priority !== "standard" && <span className="trk-pill inline-flex items-center gap-1" style={{ background: "#fdf0d5", color: "#8a5a00" }}><Zap className="h-3 w-3" />{j.priority}</span>}
            {terminal ? <span className="trk-pill" style={{ background: "#fde8e8", color: "#9b1c1c" }}>{terminalLabel}</span> : idx >= 3 && <span className="trk-pill ok">{steps[idx].label}</span>}
            {((j.billing_documents || []).some((d) => d.kind === "invoice" && !d.paid) || (j.quickbooks && !j.quickbooks.paid)) && <span className="trk-pill" style={{ background: "#fdf0d5", color: "#8a5a00" }} data-testid={`portal-invoice-due-${j.job_number}`}>Invoice due</span>}
            {j.quickbooks?.paid && <span className="trk-pill ok" data-testid={`portal-invoice-paid-${j.job_number}`}>Paid</span>}
          </div>
          <div className="trk-h2 !mb-0 mt-1.5 text-base sm:text-lg">{j.recipient_name}</div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
            {j.court_case_number && <span className="inline-flex items-center gap-1"><Scale className="h-3.5 w-3.5 text-slate-400" />{j.court_case_number}</span>}
            {j.client_job_no && <span>Your ref <b className="text-slate-700">{j.client_job_no}</b></span>}
            {j.due_date && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-slate-400" />Due {fmt(j.due_date, tz)}</span>}
            {j.court_fee && <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-900 px-2 py-0.5 font-medium" title="We paid this court fee on your behalf; it is billed back on the invoice" data-testid={`portal-court-fee-${j.job_number}`}>Court fee advanced ${Number(j.court_fee.amount).toFixed(2)}{j.court_fee.note ? ` — ${j.court_fee.note}` : ""}</span>}
          </div>
        </div>
        <div className="text-right text-xs text-slate-500 shrink-0">
          <div className="uppercase tracking-wider text-[10px] font-semibold text-slate-400">Last activity</div>
          <div className="text-sm text-slate-800 font-medium">{j.last_attempt_at ? `${fmt(j.last_attempt_at, tz)} · ${j.last_outcome}` : "No attempts yet"}</div>
          <a href={j.tracker_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[#1f5fa8] font-semibold hover:underline" data-testid={`portal-open-tracker-${j.job_number}`}>Open tracker <ArrowUpRight className="h-3.5 w-3.5" /></a>
        </div>
      </div>

      <div className={`portal-track mt-5 ${mounted ? "is-live" : ""}`}>
        <div className="portal-rail"><div className={`portal-fill ${terminal ? "is-terminal" : ""}`} style={{ width: `${pct}%` }} /></div>
        <ol className="portal-steps" style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}>
          {steps.map((s, i) => {
            const state = terminal && i === 2 ? "terminal" : i < idx ? "done" : i === idx ? "current" : "todo";
            return (
              <li key={s.key} className={`portal-step is-${state}`} data-state={state}>
                <div className="portal-node"><s.Icon className="h-3.5 w-3.5" /></div>
                <div className="portal-step-label">{terminal && i === 2 ? terminalLabel : s.label}</div>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
        <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-[#0b1f3a] rounded-full border border-slate-200 hover:border-[#0b1f3a] px-3 py-1.5 transition-colors" aria-expanded={open} data-testid={`portal-peek-${j.job_number}`}>
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />{open ? "Hide attempts" : j.attempts ? `Last ${Math.min(3, j.attempts)} attempt${Math.min(3, j.attempts) === 1 ? "" : "s"}` : "Attempts"}
          {j.attempts > 0 && <span className="font-mono text-[#1f5fa8]">{j.attempts}</span>}
        </button>
        {j.affidavit_url && (
          <a href={j.affidavit_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#0b1f3a] text-white text-xs font-semibold px-3.5 py-1.5 hover:bg-[#14305a] transition-colors" data-testid={`portal-affidavit-${j.job_number}`}>
            <Download className="h-3.5 w-3.5" />Download affidavit
          </a>
        )}
        {(j.billing_documents || []).filter((d) => d.pay_url).map((d) => (
          <a key={d.id} href={d.pay_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold px-3.5 py-1.5 hover:bg-emerald-700 transition-colors" title={`Pay ${d.name} securely through QuickBooks`} data-testid={`portal-pay-${j.job_number}`}>
            <CreditCard className="h-3.5 w-3.5" />Pay invoice
          </a>
        ))}
        {j.quickbooks?.pay_url && (
          <a href={j.quickbooks.pay_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold px-3.5 py-1.5 hover:bg-emerald-700 transition-colors" title={`Pay invoice #${j.quickbooks.invoice_number} securely through QuickBooks`} data-testid={`portal-pay-qbo-${j.job_number}`}>
            <CreditCard className="h-3.5 w-3.5" />Pay invoice #{j.quickbooks.invoice_number}
          </a>
        )}
      </div>
      {open && (
        <div className="mt-3" data-testid={`portal-attempts-${j.job_number}`}>
          {recent.length === 0 ? (
            <div className="text-sm text-slate-500 py-5 text-center border border-dashed rounded-xl">No field activity yet. Our server is gearing up — first attempts usually happen within a day or two.</div>
          ) : (
            <ol className="trk-timeline">
              {recent.map((a, i) => (
                <li key={i} className={a.served ? "is-served" : ""}>
                  <div className="trk-dot" />
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-sm">{a.label}</span><span className={`trk-pill ${a.served ? "ok" : ""}`}>{a.served ? "Served" : "Attempt"}</span>{a.service_type && <span className="text-xs text-slate-500">{a.service_type}</span>}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{fmtTime(a.attempted_at, tz)}{a.address ? ` · ${a.address}` : ""}</div>
                </li>
              ))}
              {j.attempts > 3 && <li><div className="trk-dot" /><a href={j.tracker_url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#1f5fa8] hover:underline">+{j.attempts - 3} earlier — see all on the tracker</a></li>}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
